import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@v2/components/ui/button";
import { Input } from "@v2/components/ui/input";
import { Label } from "@v2/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@v2/components/ui/select";
import { Switch } from "@v2/components/ui/switch";
import { FcIcon } from "../../components/brand";
import { PLAYER_ROLES, playerRoleLabel } from "../../lib/labels";
import { currentIntlLocale } from "../../lib/i18n";
import type { EaMemberOption } from "./PendingRequestRow";

type Mode = "closed" | "existing" | "new";

export type UnlinkedMember = {
  user_id: string;
  email: string | null;
  first_name: string | null;
  last_name: string | null;
  full_name: string | null;
  joined_at: string;
};

export function UnlinkedMemberRow({
  member,
  players,
  eaMembers,
  onLinkExisting,
  onCreateAndLink,
  onDismiss,
  isWorking,
}: {
  member: UnlinkedMember;
  players: Array<{ id: string; display_name: string; last_name: string | null; ea_sports_id: string | null; user_id: string | null }>;
  eaMembers: EaMemberOption[];
  onLinkExisting: (playerId: string) => void;
  onCreateAndLink: (payload: {
    first_name: string;
    last_name: string;
    role_code: string | null;
    shirt_number: number | null;
    ea_account_handle: string | null;
    as_guest: boolean;
  }) => void;
  onDismiss?: () => void;
  isWorking: boolean;
}) {
  const { t } = useTranslation("requests");
  // Solo i player non ancora collegati a un account sono agganciabili. Se non
  // ce ne sono (es. rosa tutta collegata), l'opzione "aggancia a esistente"
  // non ha senso e viene nascosta: resta "crea nuovo player".
  const linkablePlayers = players.filter((p) => !p.user_id);
  const [mode, setMode] = useState<Mode>("closed");
  const [playerId, setPlayerId] = useState<string>("");
  const [firstName, setFirstName] = useState(member.first_name || "");
  const [lastName, setLastName] = useState(member.last_name || "");
  const [roleCode, setRoleCode] = useState("");
  const [shirtNumber, setShirtNumber] = useState("");
  const [eaHandle, setEaHandle] = useState<string>("");
  // false = giocatore di rosa, true = provinante (is_guest, in valutazione).
  const [asGuest, setAsGuest] = useState(false);

  const submitCreate = () => {
    if (!firstName.trim() || !lastName.trim()) return;
    onCreateAndLink({
      first_name: firstName.trim(),
      last_name: lastName.trim(),
      role_code: roleCode.trim() || null,
      shirt_number: shirtNumber.trim() ? Number(shirtNumber) : null,
      ea_account_handle: eaHandle.trim() || null,
      as_guest: asGuest,
    });
  };

  return (
    <div className="border rounded-md p-3 bg-card space-y-2">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="min-w-0">
          <p className="font-semibold text-sm truncate">
            {member.full_name && member.full_name !== member.email
              ? member.full_name
              : (member.email || member.user_id.slice(0, 8) + "…")}
          </p>
          {member.full_name && member.full_name !== member.email && member.email && (
            <p className="text-xs text-muted-foreground truncate">{member.email}</p>
          )}
          <p className="text-xs text-muted-foreground">
            {t("row.joinedVia", { date: new Date(member.joined_at).toLocaleDateString(currentIntlLocale()) })}
          </p>
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          {linkablePlayers.length > 0 && (
            <Button
              size="sm"
              variant={mode === "existing" ? "default" : "outline"}
              onClick={() => setMode((m) => (m === "existing" ? "closed" : "existing"))}
            >
              {t("row.linkExisting")}
            </Button>
          )}
          <Button
            size="sm"
            variant={mode === "new" ? "default" : "outline"}
            onClick={() => setMode((m) => (m === "new" ? "closed" : "new"))}
          >
            {t("row.createNew")}
          </Button>
          {onDismiss && (
            <Button
              size="sm"
              variant="ghost"
              className="text-muted-foreground"
              title={t("row.dismissTitle")}
              disabled={isWorking}
              onClick={() => {
                const name = member.full_name || member.email || member.user_id.slice(0, 8) + "…";
                if (window.confirm(t("row.dismissConfirm", { name }))) onDismiss();
              }}
            >
              {t("row.dismiss")}
            </Button>
          )}
        </div>
      </div>

      {mode === "existing" && (
        <div className="space-y-1.5 pt-2 border-t">
          <div className="flex flex-wrap gap-1.5">
            <Select value={playerId} onValueChange={setPlayerId}>
              <SelectTrigger className="h-8 flex-1 min-w-[200px]">
                <SelectValue placeholder={t("row.selectPlayer")} />
              </SelectTrigger>
              <SelectContent>
                {linkablePlayers.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.display_name}
                    {p.ea_sports_id ? ` · EA ${p.ea_sports_id}` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              size="sm"
              className="h-8"
              disabled={!playerId || isWorking}
              onClick={() => onLinkExisting(playerId)}
            >
              {t("row.confirm")}
            </Button>
          </div>
          {(() => {
            const sel = players.find((p) => p.id === playerId);
            const isStub = !!sel && (!sel.last_name || sel.last_name.trim() === "");
            if (!isStub) return null;
            const newName = [member.first_name, member.last_name].filter(Boolean).join(" ").trim()
              || member.full_name
              || null;
            return (
              <p className="text-xs text-sky-700 bg-sky-50 border border-sky-200 rounded-md px-2 py-1">
                {t("row.stubNote")}
                {newName
                  ? t("row.stubWithName", { name: newName })
                  : t("row.stubFromUser")}
              </p>
            );
          })()}
        </div>
      )}

      {mode === "new" && (
        <div className="pt-2 border-t space-y-2">
          <p className="text-xs text-muted-foreground">
            {t("row.newPlayerIntro")}
            {member.full_name ? t("row.newPlayerPrefilled") : ""}
          </p>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label className="text-xs">{t("row.firstName")}</Label>
              <Input
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder={t("row.firstNamePh")}
                className="h-8 text-sm"
                required
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">{t("row.lastName")}</Label>
              <Input
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder={t("row.lastNamePh")}
                className="h-8 text-sm"
                required
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">{t("row.role")}</Label>
              <Select value={roleCode || "__none"} onValueChange={(v) => setRoleCode(v === "__none" ? "" : v)}>
                <SelectTrigger className="h-8 text-sm">
                  <SelectValue placeholder="—" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none">{t("row.roleNone")}</SelectItem>
                  {PLAYER_ROLES.map((r) => (
                    <SelectItem key={r} value={r}>
                      <span className="font-mono mr-2">{r}</span>
                      <span className="text-muted-foreground">{playerRoleLabel(r)}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">{t("row.number")}</Label>
              <Input
                type="number"
                min={1}
                max={99}
                value={shirtNumber}
                onChange={(e) => setShirtNumber(e.target.value)}
                placeholder={t("row.numberPh")}
                className="h-8 text-sm"
              />
            </div>
          </div>
          {eaMembers.length > 0 && (
            <div className="rounded-xl bg-violet-50 px-2.5 py-2 space-y-1.5">
              <div className="flex items-center gap-1.5">
                <FcIcon className="h-3.5 w-3.5 text-violet-500 shrink-0" />
                <span className="text-xs font-semibold text-violet-900">{t("row.eaLink")}</span>
              </div>
              <p className="text-xs text-violet-800/80 leading-snug">
                {t("row.eaLinkHint")}
              </p>
              <Select
                value={eaHandle || "__none"}
                onValueChange={(v) => setEaHandle(v === "__none" ? "" : v)}
              >
                <SelectTrigger className="h-8 text-sm bg-card">
                  <SelectValue placeholder={t("row.eaLinkPlaceholder")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none">{t("row.eaLinkNone")}</SelectItem>
                  {eaMembers.map((m) => {
                    const proLabel =
                      m.pro_name && m.pro_name !== m.ea_persona && m.pro_name !== m.ea_name
                        ? ` · Pro: ${m.pro_name}`
                        : "";
                    const nameLabel =
                      m.ea_name && m.ea_name !== m.ea_persona ? ` · ${m.ea_name}` : "";
                    return (
                      <SelectItem key={m.ea_persona} value={m.ea_persona} disabled={m.alreadyLinked}>
                        {m.ea_persona}
                        {nameLabel}
                        {proLabel}
                        {m.alreadyLinked ? t("row.eaAlreadyLinked") : ""}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
          )}
          <label className="flex items-start gap-2 rounded-2xl bg-background px-2.5 py-2 cursor-pointer">
            <Switch checked={asGuest} onCheckedChange={setAsGuest} className="mt-0.5" />
            <span className="min-w-0">
              <span className="text-xs font-medium block leading-tight">
                {t("row.createAsGuest")}
              </span>
              <span className="text-xs text-muted-foreground block leading-snug">
                {t("row.createAsGuestHint")}
              </span>
            </span>
          </label>
          <div className="flex gap-2">
            <Button
              size="sm"
              className="h-8"
              disabled={isWorking || !firstName.trim() || !lastName.trim()}
              onClick={submitCreate}
            >
              {isWorking ? t("row.creating") : t("row.createAndLink")}
            </Button>
            <Button size="sm" variant="ghost" className="h-8" onClick={() => setMode("closed")}>
              {t("row.cancel")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
